import React, { useState, useEffect, useCallback } from 'react'
import styles from './ApplicationReview.module.css'
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { Button, Chip, CircularProgress, Snackbar } from "@mui/material";

import backArrowIcon from "../../assets/images/ArrowLeftOutline.png";
import DetailsCard from '../../components/review/detailsCard/DetailsCard';
import DocumentTrustComponent from "../../components/review/documentTrust/DocumentTrustComponent";
import IdVerification from "../../components/review/idVerification/IdVerification";
import LogsTable from "../../components/review/reviewLogsTable/ReviewLogsTable";
import ReviewNotes from "../../components/review/reviewNotes/ReviewNotes";
import ReviewExitDialog from '../../components/common/dialog/reviewExitDialog/ReviewExitDialog'
import AcceptDialog from '../../components/common/dialog/acceptDialog/AcceptDialog'
import RejectDialog from '../../components/common/dialog/rejectDiolog/RejectDiolog'
import { useApplicationReview } from '../../hooks/useApplicationReview';
import useNavigationGuard from '../../hooks/useNavigationGuard';
import { useReviewContext } from '../../context/ReviewContext';
import { removeReviewLock } from '../../actions/review';
import Duplicate from '../../components/review/duplicates/Duplicate';
import CustomSnackbar from '../../components/customSnackbar/CustomSnackbar';


const ApplicationReview = () => {
    const { setIsReviewing, setApplicationStatus, shouldBlockNavigation } = useReviewContext();
    const location = useLocation();
    const navigate = useNavigate();

    const [applicationId, setApplicationId] = useState(location.state?.applicationId || sessionStorage.getItem("APPLICATION_ID"));

    // Set reviewing state on mount/unmount
    useEffect(() => {
        const storedApplicationId = sessionStorage.getItem("APPLICATION_ID");
        storedApplicationId ? setApplicationId(storedApplicationId) : navigate("/dashboard");

        setIsReviewing(true);
        fetchApplicationData();
        return () => {
            console.log("Component unmounted, removing review lock.");
            const removeLock = async () => {
                const result = await removeReviewLock(applicationId, {});
            }
            removeLock();
            sessionStorage.removeItem("APPLICATION_ID");
            setIsReviewing(false);
        };
    }, []);

    const {
        isLoading,
        error,
        applicant,
        sorData,
        sorError,
        logs,
        isAcceptModalVisible,
        isRejectModalVisible,
        duplicateData,
        isSubmitNoteFailed,
        isDecisionSubmitFailed,
        decisionSubmitError,
        setDecisionSubmitError,
        setIsDecisionSubmitFailed,
        setIsSubmitNoteFailed,
        setIsAcceptModalVisible,
        setIsRejectModalVisible,
        submitNote,
        handleApplicationDecision,
        fetchApplicationData
    } = useApplicationReview(applicationId);

    const {
        showDialog,
        handleNavigationAttempt,
        handleConfirm,
        handleCancel
    } = useNavigationGuard(shouldBlockNavigation);


    // Update application status in context
    useEffect(() => {
        if (applicant?.applicationStatus) {
            setApplicationStatus(applicant?.applicationStatus);
        }
    }, [applicant?.applicationStatus]);


    const renderStatusChip = (status) => {
        let color = "default";
        switch (status) {
            case "APPROVED":
                color = "success";
                break;
            case "PENDING":
                color = "warning";
                break;
            case "REJECTED":
                color = "error";
                break;
            default:
                color = "default";
        }
        return <Chip label={status} color={color} size="medium" />;
    };

    const handleReviewExit = useCallback(() => {
        const targetPath = applicant?.applicationStatus === 'PENDING' ||
            applicant?.applicationStatus === 'IN_REVIEW'
            ? '/dashboard/open-applications'
            : '/dashboard/closed-applications';

        handleNavigationAttempt(targetPath);
    }, [applicant?.applicationStatus, handleNavigationAttempt]);

    const handleNavigationConfirm = useCallback(() => {
        console.log("Confirm Navigation")
        setIsReviewing(false);
        handleConfirm();
    }, [setIsReviewing, handleConfirm]);

    const handleSubmitNoteErrorClose = useCallback(() => {
        setIsSubmitNoteFailed(false);
    }, [setIsSubmitNoteFailed]);

    const handleErrorDialogClose = () => {
        setIsDecisionSubmitFailed(false);
        setDecisionSubmitError('');
    };

    if (isLoading) {
        return (
            <div className={styles['loading-container']}>
                <CircularProgress sx={{ color: "#264E5A" }} />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles['error-container']}>
                <h2>Oops, Something went wrong!</h2>
                <p>{error}</p>
                <Button
                    variant='outlined'
                    onClick={() => navigate('/dashboard')}
                    sx={{
                        textTransform: 'none',
                    }}
                >
                    Go back to applications
                </Button>
            </div>
        );
    }

    return (
        <div className={styles['application-review-container']}>

            <div className={styles['applicantion-header']}>
                <div className={styles['application-short-info-container']}>

                    <Button onClick={handleReviewExit} className={styles['back-arrow']}>
                        <img src={backArrowIcon} alt="back arrow" className={styles['back-arrow']} />
                    </Button>

                    <div className={styles['applicantionid-container']}>
                        <span className={styles['applicantion-id-text']}>Applicant ID</span>
                        <span className={styles['applicantion-id']}>{applicationId}</span>
                    </div>

                    {renderStatusChip(applicant?.applicationStatus)}
                </div>
            </div>

            <DetailsCard
                name="documentInfo"
                cardTitle="Document Info"
                detailsData={{
                    documentNumber: applicant?.primaryDocument?.documentNumber,
                    cardSerialNumber: applicant?.primaryDocument?.cardSerialNumber,
                    DocumentType: applicant?.primaryDocument?.ocrDocumentType,
                    userSelectedDocumentType: applicant?.primaryDocument?.userInputDocumentType,
                    ...applicant?.primaryDocument?.docAttributes
                }}
                ocrData={applicant?.ocrAttributes}
                comparisonData={applicant?.barcodeAttributes}
                comparisonData2={sorData}
                enableThreeWayComparison={!!(applicant?.barcodeAttributes && sorData)}
                source1Name="OCR"
                source2Name="Barcode"
                source3Name="SOR/MV"
            />

            <DetailsCard
                name="identityInfo"
                cardTitle="Identity Info"
                detailsData={{
                    ...applicant?.primaryDocument?.idAttributes
                }}
                ocrData={applicant?.ocrAttributes}
                comparisonData={applicant?.barcodeAttributes}
                comparisonData2={sorData}
                enableThreeWayComparison={!!(applicant?.barcodeAttributes && sorData)}
                source1Name="OCR"
                source2Name="Barcode"
                source3Name="SOR/MV"
            />

            <IdVerification
                selfie={applicant?.biometrics?.selfie}
                sorPortrait={sorData?.portrait}
                primaryDocument={applicant?.primaryDocument}
                secondaryDocument={applicant?.secondaryDocument}
                documentPortrait={applicant?.primaryDocument?.documentPortrait}
                primaryFrontImage={applicant?.primaryDocument?.frontImage}
                primaryBackImage={applicant?.primaryDocument?.backImage}
                secondaryFrontImage={applicant?.secondaryDocument?.frontImage} />

            <DocumentTrustComponent
                title="Biometrics Trust"
                items={applicant?.biometrics} />

            <DocumentTrustComponent
                title="Document Trust"
                items={applicant?.primaryDocument?.docAuthDetails}
            />

            <DetailsCard
                name="sorInfo"
                cardTitle="System of Record (MV Registry)"
                detailsData={sorData}
                sorError={sorError}
            />

            <Duplicate data={duplicateData} />

            <div className={styles['logs-and-notes']}>
                <LogsTable logs={logs} />
                <ReviewNotes applicationStatus={applicant?.applicationStatus} onSubmitNote={submitNote} />
                <CustomSnackbar
                    open={isSubmitNoteFailed}
                    onClose={handleSubmitNoteErrorClose}
                    message="Error submitting note. Please try again."
                />
            </div>

            <div className={styles['submit-buttons']}>
                <Button
                    variant="contained"
                    color="success"
                    disabled={
                        applicant?.applicationStatus === "APPROVED" ||
                        applicant?.applicationStatus === "REJECTED"}
                    sx={{
                        padding: '0.75rem 2.25rem',
                        textTransform: 'none',
                    }}
                    onClick={() => setIsAcceptModalVisible(true)}>Approve</Button>
                <Button
                    variant="contained"
                    disabled={
                        applicant?.applicationStatus === "APPROVED" ||
                        applicant?.applicationStatus === "REJECTED"}
                    sx={{
                        padding: '0.75rem 2.25rem',
                        backgroundColor: "#DC4405",
                        textTransform: 'none',
                    }}
                    onClick={() => setIsRejectModalVisible(true)}>Reject</Button>
            </div>

            <ReviewExitDialog
                isOpen={showDialog}
                onClose={handleCancel}
                onConfirm={handleNavigationConfirm} />

            <AcceptDialog
                isOpen={isAcceptModalVisible}
                onClose={() => setIsAcceptModalVisible(false)}
                onConfirm={(loaLevel) => handleApplicationDecision('APPROVE', loaLevel, "Application reviewed.")} />

            <RejectDialog
                isOpen={isRejectModalVisible}
                onClose={() => setIsRejectModalVisible(false)}
                submitNote={submitNote}
                onConfirm={(reasons) => handleApplicationDecision('REJECT', 1, reasons)} />

            <CustomSnackbar
                open={isDecisionSubmitFailed}
                message={decisionSubmitError || 'Error submitting decision. Please try again.'}
                onClose={handleErrorDialogClose}
            />
        </div>
    )
}

export default ApplicationReview